import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  ClipboardDocumentListIcon,
  FolderIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowLeftIcon,
  TagIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
  ClipboardIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PauseIcon,
  BookOpenIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  CodeBracketIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  PlayIcon,
  FlagIcon,
  SparklesIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  BellIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const StudiesControl: React.FC = () => {
  const pageUrl = 'https://upmindy.com/docs/studies';
  const pageTitle = 'Módulo de Estudos | Upmindy';
  const pageDescription = 'Organize e gerencie todos os seus recursos de aprendizado em um só lugar. Acompanhe progresso, defina prioridades e mantenha seus estudos estruturados.';

  return (
    <div className="prose dark:prose-invert max-w-none">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <Link 
        to="/docs" 
        className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 no-underline"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Voltar para Documentação
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Módulo de Estudos
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
          Crie conteúdo de estudo com IA e gerencie todos os seus recursos de aprendizado em um só lugar. Acompanhe progresso, defina prioridades e mantenha seus estudos estruturados.
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Módulos de Estudo com Inteligência Artificial</h2>

      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-8 rounded-2xl border border-indigo-100 dark:border-gray-700 shadow-sm mb-8 not-prose">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
            <SparklesIcon className="h-6 w-6 text-white" />
          </div>
          <h3 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
            Deixe a IA montar seu plano de estudos
          </h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
          Em vez de descobrir sozinho por onde começar, peça para a IA transformar qualquer assunto em um 
          currículo completo, com módulos sequenciais e conteúdo de estudo pronto para consumir.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <RectangleStackIcon className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              <h4 className="font-bold text-gray-900 dark:text-gray-100">Gerar Módulos Automaticamente</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Com um clique, a IA analisa o assunto e a descrição do seu estudo e cria uma lista progressiva de 
              módulos (do básico ao avançado), cobrindo os tópicos necessários para dominar o tema.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
              <h4 className="font-bold text-gray-900 dark:text-gray-100">Gerar Conteúdo do Módulo</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Para cada módulo, peça à IA para escrever um texto de estudo completo e didático em Markdown, 
              com explicações, exemplos práticos e um resumo dos pontos-chave.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <CheckCircleIcon className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
              <h4 className="font-bold text-gray-900 dark:text-gray-100">Progresso por Módulo</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Marque cada módulo como não iniciado, em progresso ou concluído. Ao começar o primeiro módulo, 
              o estudo inteiro é automaticamente movido para "em progresso".
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <SparklesIcon className="h-5 w-5 mr-2 text-amber-600 dark:text-amber-400" />
              <h4 className="font-bold text-gray-900 dark:text-gray-100">Uso Gratuito e Premium</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Contas gratuitas têm um limite de módulos com conteúdo gerado por IA. Assinantes Premium geram 
              conteúdo ilimitado. Regenerar o conteúdo de um módulo já gerado não consome o limite novamente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudiesControl;
